import React from 'react';
import { Link } from 'react-router-dom';
import './Platform.css';

const Platform = () => {
  return (
    <div className="platform-page">
      {/* Hero Section */}
      <section className="platform-hero">
        <div className="container">
          <div className="hero-content">
            <h1>The Pathway Platform</h1>
            <p className="hero-subtitle">
              One stack for apps, data, and governance—designed for human-centered work.
            </p>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="section">
        <div className="container">
          <div className="overview-content">
            <p className="overview-text">
              Most organizations doing the hardest work are stuck with the worst systems.
              The Pathway Platform brings structure, reliability, and compliance to the programs,
              people, and data you manage every day.
            </p>
          </div>
        </div>
      </section>

      {/* Application Layer */}
      <section className="section section-alt">
        <div className="container">
          <div className="layer-section">
            <div className="layer-header">
              <div className="layer-icon app-layer-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="7" height="7" rx="1" />
                  <rect x="14" y="3" width="7" height="7" rx="1" />
                  <rect x="14" y="14" width="7" height="7" rx="1" />
                  <rect x="3" y="14" width="7" height="7" rx="1" />
                </svg>
              </div>
              <h2>Application Layer</h2>
            </div>
            <p className="layer-intro">
              We build modular applications that map directly to how your work actually happens:
            </p>
            <ul className="feature-list">
              <li>Program and case management</li>
              <li>Staff and volunteer workflows</li>
              <li>Training and credential tracking</li>
              <li>Intake, assessments, and follow-up</li>
              <li>Task management and internal coordination</li>
            </ul>
            <p className="layer-conclusion">
              Each app is designed to be simple for front-line staff and powerful enough for leadership.
            </p>
          </div>
        </div>
      </section>

      {/* Data & Evidence Layer */}
      <section className="section">
        <div className="container">
          <div className="layer-section">
            <div className="layer-header">
              <div className="layer-icon data-layer-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 3v18h18" />
                  <path d="M18 9l-5 5-4-4-3 3" />
                </svg>
              </div>
              <h2>Data & Evidence Layer</h2>
            </div>
            <p className="layer-intro">
              Your work generates data that should be usable, not buried. The data layer:
            </p>
            <ul className="feature-list">
              <li>Connects people, programs, activities, and outcomes</li>
              <li>Standardizes fields across apps and departments</li>
              <li>Supports grant reporting and compliance reviews</li>
              <li>Enables longitudinal analysis and trend monitoring</li>
              <li>Provides dashboards for boards, funders, and leadership</li>
            </ul>
            <p className="layer-conclusion">
              You keep ownership of your data. We help you use it.
            </p>
          </div>
        </div>
      </section>

      {/* AI Governance Layer */}
      <section className="section section-alt">
        <div className="container">
          <div className="layer-section">
            <div className="layer-header">
              <div className="layer-icon ai-layer-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
              </div>
              <h2>AI Governance Layer</h2>
            </div>
            <p className="layer-intro">
              We design systems assuming AI will be part of your workflow—and that it must be controlled:
            </p>
            <ul className="feature-list">
              <li>Role-based access and permissions</li>
              <li>Policy engines that define where and how AI can be used</li>
              <li>Event logging for AI-assisted actions</li>
              <li>Guardrails to reduce risk in sensitive contexts</li>
              <li>Configurations aligned with your legal and policy guidance</li>
            </ul>
            <p className="layer-conclusion">
              AI becomes a tool that supports your staff, not a risk they have to fear.
            </p>
          </div>
        </div>
      </section>

      {/* Continuity Section */}
      <section className="section continuity-section">
        <div className="container">
          <div className="continuity-content">
            <h2>Built for continuity</h2>
            <p className="continuity-intro">
              Leadership changes. Staff turns over. Programs evolve.<br />
              The platform keeps:
            </p>
            <ul className="continuity-list">
              <li>Your data consistent</li>
              <li>Your workflows documented</li>
              <li>Your training requirements tracked</li>
              <li>Your institutional memory intact</li>
            </ul>
            <p className="continuity-conclusion">
              We build as if your organization will be here 50–500 years from now.
            </p>
          </div>
        </div>
      </section>

      {/* Platform Disclaimers */}
      <section className="section platform-disclaimers">
        <div className="container">
          <div className="platform-disclaimers-content">
            <h3>Important Notes</h3>
            <div className="platform-disclaimer-grid">
              <div className="platform-disclaimer-item">
                <h4>AI-Assisted Features</h4>
                <p>
                  Some tools may include AI-assisted features. AI outputs are suggestions only
                  and should not be relied upon as factual, legal, or clinical determinations.
                  Users must review and validate all AI-generated information.
                </p>
              </div>
              <div className="platform-disclaimer-item">
                <h4>Compliance Automation</h4>
                <p>
                  Any automation or workflow provided by Pathway Labs is intended to assist
                  but not replace human oversight. Pathway Labs does not guarantee that automated
                  workflows meet any specific regulatory standard (HIPAA, FERPA, COPPA, OMB 2 CFR 200, etc.).
                </p>
              </div>
              <div className="platform-disclaimer-item">
                <h4>Data Handling</h4>
                <p>
                  Pathway Labs tools are designed for secure environments; however, customers are
                  responsible for configuring access controls, encrypting sensitive data, and ensuring
                  compliance with applicable laws. Pathway Labs does not control how customers store or use their data.
                </p>
              </div>
              <div className="platform-disclaimer-item">
                <h4>Software Use</h4>
                <p>
                  Use of Pathway Labs tools does not ensure compliance with any law or regulation.
                  Organizations are solely responsible for ensuring the accuracy and appropriateness
                  of the information they input and the regulatory requirements they must satisfy.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">See the platform in your context</h2>
            <p className="cta-text">
              We'll walk through how the Pathway Platform would apply to your specific programs and constraints.
            </p>
            <Link to="/contact" className="btn btn-primary btn-large">Schedule a Call</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Platform;
